import {Component, inject, Input} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn
} from '@angular/forms';
import {MaterialModule} from '../../../../material.module';

export type ProductFeedFieldMapping = Record<string, Record<string, string>>;
export const maximumFieldMappingFields = 64;
export const maximumFieldMappingProperties = 16;

function safeText(maxLength: number): ValidatorFn {
  return control => {
    const value = String(control.value ?? '');
    if (!value.trim()) return {required: true};
    if (value.trim().length > maxLength) return {maxlength: true};
    return Array.from(value).some(character => {
      const code = character.charCodeAt(0);
      return code <= 31 || (code >= 127 && code <= 159);
    }) ? {controlCharacters: true} : null;
  };
}

export function uniqueChildKeyValidator(controlName: string): ValidatorFn {
  return control => {
    if (!(control instanceof FormArray)) return null;
    const values = control.controls
      .map(item => String(item.get(controlName)?.value ?? '').trim().toLowerCase())
      .filter(Boolean);
    return new Set(values).size === values.length ? null : {duplicateKeys: true};
  };
}

export function maximumArrayLengthValidator(maximum: number): ValidatorFn {
  return control => control instanceof FormArray && control.length > maximum ? {maximumItems: true} : null;
}

export function minimumArrayLengthValidator(minimum: number): ValidatorFn {
  return control => control instanceof FormArray && control.length < minimum ? {minimumItems: true} : null;
}

export function createFieldMappingPropertyGroup(
  fb: FormBuilder,
  key = '',
  value = ''
): FormGroup {
  return fb.group({
    key: [key, [safeText(64)]],
    value: [value, [safeText(2048)]]
  });
}

export function createFieldMappingEntryGroup(
  fb: FormBuilder,
  outputName = '',
  properties: Record<string, string> = {path: ''}
): FormGroup {
  return fb.group({
    outputName: [outputName, [safeText(64)]],
    properties: fb.array(
      Object.entries(properties).map(([key, value]) => createFieldMappingPropertyGroup(fb, key, value)),
      [
        uniqueChildKeyValidator('key'),
        minimumArrayLengthValidator(1),
        maximumArrayLengthValidator(maximumFieldMappingProperties)
      ]
    )
  });
}

@Component({
  selector: 'app-standard-field-mapping',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule],
  templateUrl: './standard-field-mapping.component.html',
  styleUrls: ['./standard-field-mapping.component.scss']
})
export class StandardFieldMappingComponent {
  private readonly fb = inject(FormBuilder);
  readonly maximumFields = maximumFieldMappingFields;
  readonly maximumProperties = maximumFieldMappingProperties;

  @Input({required: true}) mappingForm!: FormGroup;
  @Input() defaults: ProductFeedFieldMapping = {};

  get fields(): FormArray {
    return this.mappingForm.get('fields') as FormArray;
  }

  get defaultEntries(): Array<{outputName: string; properties: Record<string, string>}> {
    return Object.entries(this.defaults).map(([outputName, properties]) => ({outputName, properties}));
  }

  properties(field: AbstractControl): FormArray {
    return field.get('properties') as FormArray;
  }

  addField(): void {
    if (this.fields.length >= maximumFieldMappingFields) return;
    this.fields.push(createFieldMappingEntryGroup(this.fb));
    this.mappingForm.markAsDirty();
    this.mappingForm.markAsTouched();
  }

  removeField(index: number): void {
    this.fields.removeAt(index);
    this.mappingForm.markAsDirty();
    this.mappingForm.markAsTouched();
  }

  addProperty(fieldIndex: number): void {
    const properties = this.properties(this.fields.at(fieldIndex));
    if (properties.length >= maximumFieldMappingProperties) return;
    properties.push(createFieldMappingPropertyGroup(this.fb));
    this.mappingForm.markAsDirty();
    this.mappingForm.markAsTouched();
  }

  removeProperty(fieldIndex: number, propertyIndex: number): void {
    const properties = this.properties(this.fields.at(fieldIndex));
    if (properties.length <= 1) return;
    properties.removeAt(propertyIndex);
    this.mappingForm.markAsDirty();
    this.mappingForm.markAsTouched();
  }

  useEditableDefaults(): void {
    this.fields.clear();
    for (const [outputName, properties] of Object.entries(this.defaults)) {
      this.fields.push(createFieldMappingEntryGroup(this.fb, outputName, properties));
    }
    this.mappingForm.markAsDirty();
    this.mappingForm.markAsTouched();
  }

  propertySummary(properties: Record<string, string>): string {
    return Object.entries(properties).map(([key, value]) => `${key}: ${value}`).join(' · ');
  }
}
